import type { TFunction } from 'i18next'
import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Plus,
  Pencil,
  Trash2,
  Check,
  X,
  Loader2,
  Tag,
  ChevronRight,
  ChevronDown,
} from 'lucide-react'
import { EmptyState } from '@/components/EmptyState'
import { cn } from '@/lib/utils'
import { useLabels, useCreateLabel, useUpdateLabel, useDeleteLabel } from '@/hooks/use-labels'
import type { Label } from '@/lib/types'

const PRESET_COLORS = [
  '#ef4444', // red
  '#f97316', // orange
  '#eab308', // yellow
  '#22c55e', // green
  '#06b6d4', // cyan
  '#3b82f6', // blue
  '#DCCBF5', // lilac
  '#ec4899', // pink
  '#6b7280', // gray
]

function ColorPicker({ value, onChange }: { value: string; onChange: (color: string) => void }) {
  return (
    <div className="flex flex-wrap gap-2">
      {PRESET_COLORS.map((color) => (
        <button
          key={color}
          type="button"
          onClick={() => onChange(color)}
          className={cn(
            'h-7 w-7 rounded-full border-2 transition-transform',
            value === color ? 'border-foreground scale-110' : 'border-transparent',
          )}
          style={{ backgroundColor: color }}
        />
      ))}
    </div>
  )
}

interface LabelTree extends Label {
  children: LabelTree[]
}

function buildTree(labels: Label[]): LabelTree[] {
  const map = new Map<string, LabelTree>()
  const roots: LabelTree[] = []

  for (const label of labels) {
    map.set(label.id, { ...label, children: [] })
  }

  for (const label of labels) {
    const node = map.get(label.id)!
    if (label.parentId && map.has(label.parentId)) {
      map.get(label.parentId)!.children.push(node)
    } else {
      roots.push(node)
    }
  }

  // Sort children by sortOrder
  for (const node of map.values()) {
    node.children.sort((a, b) => a.sortOrder - b.sortOrder)
  }
  roots.sort((a, b) => a.sortOrder - b.sortOrder)

  return roots
}

function LabelRow({
  label,
  depth,
  isExpanded,
  hasChildren,
  onToggleExpand,
  editingId,
  editName,
  editColor,
  setEditName,
  setEditColor,
  deletingId,
  onStartEdit,
  onUpdate,
  onCancelEdit,
  onStartDelete,
  onConfirmDelete,
  onCancelDelete,
  onNavigate,
  onAddChild,
  updatePending,
  deletePending,
  t,
}: {
  label: Label
  depth: number
  isExpanded: boolean
  hasChildren: boolean
  onToggleExpand: () => void
  editingId: string | null
  editName: string
  editColor: string
  setEditName: (v: string) => void
  setEditColor: (v: string) => void
  deletingId: string | null
  onStartEdit: (label: Label) => void
  onUpdate: () => void
  onCancelEdit: () => void
  onStartDelete: (id: string) => void
  onConfirmDelete: (id: string) => void
  onCancelDelete: () => void
  onNavigate: (labelId: string) => void
  onAddChild: (parentId: string) => void
  updatePending: boolean
  deletePending: boolean
  t: TFunction
}) {
  const indent = depth * 24

  return (
    <li>
      {editingId === label.id ? (
        <div className="space-y-3 p-4" style={{ paddingLeft: 16 + indent }}>
          <input
            type="text"
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            className="border-outline-variant/30 bg-surface-container focus:ring-ring w-full rounded-lg border px-3 py-2 text-sm outline-none focus:ring-2"
            onKeyDown={(e) => {
              if (e.key === 'Enter') onUpdate()
              if (e.key === 'Escape') onCancelEdit()
            }}
            autoFocus
          />
          <ColorPicker value={editColor} onChange={setEditColor} />
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onUpdate}
              disabled={!editName.trim() || updatePending}
              className="bg-primary text-primary-foreground flex items-center gap-1 rounded-lg px-3 py-1.5 text-sm font-medium transition-opacity hover:opacity-90 disabled:opacity-50"
            >
              {updatePending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Check className="h-3.5 w-3.5" />
              )}
              {t('common.save', 'Save')}
            </button>
            <button
              type="button"
              onClick={onCancelEdit}
              className="border-outline-variant/30 hover:bg-surface-container-high flex items-center gap-1 rounded-lg border px-3 py-1.5 text-sm font-medium"
            >
              <X className="h-3.5 w-3.5" />
              {t('common.cancel', 'Cancel')}
            </button>
          </div>
        </div>
      ) : deletingId === label.id ? (
        <div className="flex items-center justify-between p-4" style={{ paddingLeft: 16 + indent }}>
          <p className="text-on-surface-variant text-sm">
            {t('labels.confirmDelete', 'Delete "{{name}}"?', { name: label.name })}
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => onConfirmDelete(label.id)}
              disabled={deletePending}
              className="bg-destructive text-destructive-foreground flex items-center gap-1 rounded-lg px-3 py-1.5 text-sm font-medium transition-opacity hover:opacity-90 disabled:opacity-50"
            >
              {deletePending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              {t('common.delete', 'Delete')}
            </button>
            <button
              type="button"
              onClick={onCancelDelete}
              className="border-outline-variant/30 hover:bg-surface-container-high rounded-lg border px-3 py-1.5 text-sm font-medium"
            >
              {t('common.cancel', 'Cancel')}
            </button>
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-1 p-4" style={{ paddingLeft: 8 + indent }}>
          {/* Expand/collapse toggle for parents */}
          {hasChildren ? (
            <button
              type="button"
              onClick={onToggleExpand}
              className="hover:bg-surface-container-high flex h-6 w-6 shrink-0 items-center justify-center rounded"
            >
              {isExpanded ? (
                <ChevronDown className="text-on-surface-variant h-4 w-4" />
              ) : (
                <ChevronRight className="text-on-surface-variant h-4 w-4" />
              )}
            </button>
          ) : (
            <span className="w-6 shrink-0" />
          )}
          <button
            type="button"
            onClick={() => onNavigate(label.id)}
            className="flex flex-1 items-center gap-3"
          >
            <span
              className="h-3.5 w-3.5 shrink-0 rounded-full"
              style={{ backgroundColor: label.color ?? '#6b7280' }}
            />
            <span className="flex-1 text-left text-sm font-medium">{label.name}</span>
          </button>
          <div className="flex gap-1">
            {/* Add sub-label — only for root labels */}
            {depth === 0 && (
              <button
                type="button"
                onClick={() => onAddChild(label.id)}
                className="hover:bg-surface-container-high flex h-8 w-8 items-center justify-center rounded-lg"
                title={t('labels.addChild', 'Add sub-label')}
              >
                <Plus className="text-on-surface-variant h-4 w-4" />
              </button>
            )}
            <button
              type="button"
              onClick={() => onStartEdit(label)}
              className="hover:bg-surface-container-high flex h-8 w-8 items-center justify-center rounded-lg"
            >
              <Pencil className="text-on-surface-variant h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => onStartDelete(label.id)}
              className="hover:bg-surface-container-high flex h-8 w-8 items-center justify-center rounded-lg"
            >
              <Trash2 className="text-on-surface-variant h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </li>
  )
}

export default function LabelsPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { data: labels, isLoading } = useLabels()
  const createLabel = useCreateLabel()
  const updateLabel = useUpdateLabel()
  const deleteLabel = useDeleteLabel()

  const [showAddForm, setShowAddForm] = useState(false)
  const [addParentId, setAddParentId] = useState<string | null>(null)
  const [newName, setNewName] = useState('')
  const [newColor, setNewColor] = useState(PRESET_COLORS[5])

  const [editingId, setEditingId] = useState<string | null>(null)
  const [editName, setEditName] = useState('')
  const [editColor, setEditColor] = useState('')

  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set())

  const tree = useMemo(() => buildTree(labels ?? []), [labels])

  function toggleExpand(id: string) {
    setExpandedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  function handleShowAddChild(parentId: string) {
    setAddParentId(parentId)
    setShowAddForm(true)
    setNewName('')
    setNewColor(PRESET_COLORS[5])
    // Ensure parent is expanded
    setExpandedIds((prev) => new Set(prev).add(parentId))
  }

  function handleShowAddRoot() {
    setAddParentId(null)
    setShowAddForm(!showAddForm)
    setNewName('')
    setNewColor(PRESET_COLORS[5])
  }

  function handleCreate() {
    const name = newName.trim()
    if (!name) return

    createLabel.mutate(
      { name, color: newColor, parentId: addParentId },
      {
        onSuccess: () => {
          setNewName('')
          setNewColor(PRESET_COLORS[5])
          setShowAddForm(false)
          setAddParentId(null)
        },
      },
    )
  }

  function startEdit(label: Label) {
    setEditingId(label.id)
    setEditName(label.name)
    setEditColor(label.color ?? PRESET_COLORS[5])
  }

  function handleUpdate() {
    if (!editingId) return
    const name = editName.trim()
    if (!name) return

    updateLabel.mutate(
      { id: editingId, name, color: editColor },
      {
        onSuccess: () => {
          setEditingId(null)
        },
      },
    )
  }

  function handleDelete(id: string) {
    deleteLabel.mutate(id, {
      onSuccess: () => {
        setDeletingId(null)
      },
    })
  }

  function renderAddForm(parentId: string | null) {
    if (!showAddForm || addParentId !== parentId) return null
    const indent = parentId ? 24 : 0

    return (
      <div className="border-outline-variant/20 border-b p-4" style={{ paddingLeft: 16 + indent }}>
        <div className="space-y-3">
          <input
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder={
              parentId
                ? t('labels.subLabelPlaceholder', 'Sub-label name...')
                : t('labels.namePlaceholder', 'Label name...')
            }
            className="border-outline-variant/30 bg-surface-container focus:ring-ring w-full rounded-lg border px-3 py-2 text-sm outline-none focus:ring-2"
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleCreate()
              if (e.key === 'Escape') {
                setShowAddForm(false)
                setAddParentId(null)
              }
            }}
            autoFocus
          />
          <ColorPicker value={newColor} onChange={setNewColor} />
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleCreate}
              disabled={!newName.trim() || createLabel.isPending}
              className="bg-primary text-primary-foreground flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-opacity hover:opacity-90 disabled:opacity-50"
            >
              {createLabel.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              {parentId ? t('labels.addChild', 'Add Sub-label') : t('labels.add', 'Add Label')}
            </button>
            <button
              type="button"
              onClick={() => {
                setShowAddForm(false)
                setAddParentId(null)
              }}
              className="border-outline-variant/30 hover:bg-surface-container-high flex items-center gap-1 rounded-lg border px-3 py-1.5 text-sm font-medium"
            >
              <X className="h-3.5 w-3.5" />
              {t('common.cancel', 'Cancel')}
            </button>
          </div>
        </div>
      </div>
    )
  }

  function renderTreeNode(node: LabelTree, depth: number) {
    const isExpanded = expandedIds.has(node.id)
    const hasChildren = node.children.length > 0

    return (
      <div key={node.id}>
        <LabelRow
          label={node}
          depth={depth}
          isExpanded={isExpanded}
          hasChildren={hasChildren}
          onToggleExpand={() => toggleExpand(node.id)}
          editingId={editingId}
          editName={editName}
          editColor={editColor}
          setEditName={setEditName}
          setEditColor={setEditColor}
          deletingId={deletingId}
          onStartEdit={startEdit}
          onUpdate={handleUpdate}
          onCancelEdit={() => setEditingId(null)}
          onStartDelete={setDeletingId}
          onConfirmDelete={handleDelete}
          onCancelDelete={() => setDeletingId(null)}
          onNavigate={(id) => navigate(`/contacts?label_id=${id}`)}
          onAddChild={handleShowAddChild}
          updatePending={updateLabel.isPending}
          deletePending={deleteLabel.isPending}
          t={t}
        />
        {isExpanded && hasChildren && (
          <ul className="divide-outline-variant/10 divide-y">
            {node.children.map((child) => renderTreeNode(child, depth + 1))}
          </ul>
        )}
        {/* Add child form appears after the parent */}
        {renderAddForm(node.id)}
      </div>
    )
  }

  return (
    <div className="flex flex-1 flex-col">
      {/* Header */}
      <div className="border-outline-variant/20 flex items-center justify-between border-b px-6 py-4">
        <h1 className="font-heading text-lg font-semibold">{t('labels.title', 'Labels')}</h1>
        <button
          type="button"
          onClick={handleShowAddRoot}
          className="hover:bg-surface-container-high flex h-8 w-8 items-center justify-center rounded-lg"
        >
          {showAddForm && addParentId === null ? (
            <X className="h-5 w-5" />
          ) : (
            <Plus className="h-5 w-5" />
          )}
        </button>
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto max-w-lg">
          {/* Add root label form */}
          {renderAddForm(null)}

          {/* Loading */}
          {isLoading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="text-on-surface-variant h-6 w-6 animate-spin" />
            </div>
          )}

          {/* Empty state */}
          {!isLoading && labels && labels.length === 0 && (
            <EmptyState
              icon={Tag}
              title={t('labels.empty', 'No labels yet')}
              description={t('labels.emptyHint', 'Create a label to tag your contacts.')}
              ctaLabel={t('labels.add', 'Add Label')}
              onCtaClick={() => handleShowAddRoot()}
            />
          )}

          {/* Label tree */}
          {tree.length > 0 && (
            <ul className="divide-outline-variant/20 divide-y">
              {tree.map((node) => renderTreeNode(node, 0))}
            </ul>
          )}
        </div>
      </div>
    </div>
  )
}
